import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

interface BoostsData {
  availableBoosts: number;
  usedBoosts: number;
  lastCreditedAt: string | null;
}

export const useBoosts = () => {
  const { user } = useAuth();
  const [boostsData, setBoostsData] = useState<BoostsData>({
    availableBoosts: 0,
    usedBoosts: 0,
    lastCreditedAt: null
  });
  const [loading, setLoading] = useState(true);

  const refreshBoosts = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('user_boosts')
        .select('available_boosts, used_boosts, last_credited_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        debugLogger.error('Boosts', 'Error fetching boosts:', error);
        return;
      }

      if (data) {
        setBoostsData({
          availableBoosts: data.available_boosts,
          usedBoosts: data.used_boosts,
          lastCreditedAt: data.last_credited_at
        });
      } else {
        // Create initial record if doesn't exist
        const { error: insertError } = await supabase
          .from('user_boosts')
          .insert({ user_id: user.id, available_boosts: 0, used_boosts: 0 });

        if (insertError) {
          debugLogger.error('Boosts', 'Error creating boosts record:', insertError);
        } else {
          setBoostsData({
            availableBoosts: 0,
            usedBoosts: 0,
            lastCreditedAt: null
          });
        }
      }

      debugLogger.debug('Boosts', 'Boosts data updated:', boostsData);

    } catch (error) {
      debugLogger.error('Boosts', 'Error refreshing boosts:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const useBoost = useCallback(async (reason: string = 'profile_highlight') => {
    if (!user?.id) {
      toast.error('Você precisa estar logado para usar um boost');
      return false;
    }

    if (boostsData.availableBoosts <= 0) {
      toast.error('Você não tem boosts disponíveis');
      return false;
    }

    try {
      const { error } = await supabase.rpc('use_user_boost', {
        p_user_id: user.id,
        p_reason: reason
      });

      if (error) {
        debugLogger.error('Boosts', 'Error using boost:', error);
        toast.error('Erro ao usar boost. Tente novamente.');
        return false;
      }

      // Refresh boosts data
      await refreshBoosts();
      toast.success('Boost ativado com sucesso! 🚀');
      return true;

    } catch (error) {
      debugLogger.error('Boosts', 'Error using boost:', error);
      toast.error('Erro ao usar boost. Tente novamente.');
      return false;
    }
  }, [user?.id, boostsData.availableBoosts, refreshBoosts]);

  const buyAndUseBoost = useCallback(async () => {
    if (!user?.id) {
      toast.error('Você precisa estar logado');
      return false;
    }

    try {
      const { data: success, error } = await supabase.rpc('buy_and_use_boost', {
        p_user_id: user.id
      });

      if (error) {
        debugLogger.error('Boosts', 'Error buying boost:', error);
        toast.error('Erro ao comprar boost. Verifique seu saldo de gemas.');
        return false;
      }

      if (!success) {
        toast.error('Gemas insuficientes para comprar boost');
        return false;
      }

      await refreshBoosts();
      toast.success('Boost comprado e ativado! 🚀💎');
      return true;

    } catch (error) {
      debugLogger.error('Boosts', 'Error buying boost:', error);
      toast.error('Erro ao comprar boost.');
      return false;
    }
  }, [user?.id, refreshBoosts]);

  useEffect(() => {
    refreshBoosts();
  }, [refreshBoosts]);

  return {
    availableBoosts: boostsData.availableBoosts,
    usedBoosts: boostsData.usedBoosts,
    lastCreditedAt: boostsData.lastCreditedAt,
    useBoost,
    buyAndUseBoost,
    refreshBoosts,
    loading
  };
};
